import { useDispatch, useSelector } from 'react-redux';
import { State } from 'reducers';
import { Dialog } from 'ui/shared/containers';
import { getBuilding } from 'selectors/buildingsSelectors';
import { useCallback, useState } from 'react';
import { BuildingId, Device } from 'shared/models';
import { createDevicesSuccess } from 'actions/deviceActions';
import { callApi } from 'apiClient';
import { useTranslation } from 'react-i18next';
import { toError } from 'shared/util/toError';
import { useDeviceTypes } from 'hooks/useDeviceTypes/useDeviceTypes';
import { useAsync } from 'hooks';
import { AddDevicesForm, FormData } from "./addDevicesForm/AddDevicesForm";

require('./AddDevicesDialog.css');

export type Props = {
    visible: boolean;
    buildingId: BuildingId;
    onClose(): void;
    initialError?: string; // For Storybook
};

export const AddDevicesDialog = ({ visible, buildingId, onClose, initialError = '' }: Props) => {
    const { t } = useTranslation();
    const { isMounted } = useAsync();

    const building = useSelector((state: State) => getBuilding(state, { buildingId }));
    const urlSlug = building && building.urlSlug;

    const { deviceTypes, isLoadingDeviceTypes } = useDeviceTypes();

    const dispatch = useDispatch();

    const [ busy, setBusy ] = useState(false);
    const [ errorMessage, setErrorMessage ] = useState(initialError);

    const handleClickCreate = useCallback(({ deviceFamily, elevatorId, groupName, pi, ...data }: FormData) => {
        if (!urlSlug || !buildingId) return;
        setBusy(true);
        void (async () => {
            try {
                const payload = {
                    ...data,
                    groupName: groupName || null,
                    deviceClass: data.deviceClass,
                    properties: {
                        ...data.properties,
                        elevatorId,
                        pi,
                    },
                };
                const result: Device | Device[] = await callApi('POST', `/api/buildings/${buildingId}/devices`, payload);
                const devices = Array.isArray(result) ? result : [ result ];
                dispatch(createDevicesSuccess(devices, { buildingId }));
                onClose();
            }
            catch (e) {
                if (!isMounted()) return;
                const err = toError(e);
                const errorMessage = t([ `DEVICES.ERROR.${err.message}`, 'SERVER_ERROR' ]);
                setErrorMessage(errorMessage);
                setBusy(false);
                return;
            }
        })();
    }, [ urlSlug, buildingId, dispatch, setBusy, setErrorMessage, t, isMounted ]);

    // Don't show any UI until the data has loaded
    if (!building || isLoadingDeviceTypes) {
        return null;
    }
    return (
        <Dialog
            id="add-devices-dialog"
            visible={ visible }
            titleKey={ `DEVICES.EDIT_DIALOG.NEW_TITLE` }
        >
            <AddDevicesForm
                busy={ busy }
                building={ building }
                deviceTypes={ deviceTypes }
                errorMessage={ errorMessage }
                onClickCreate={ handleClickCreate }
                onClickCancel={ onClose }
            />
        </Dialog>
    );
};
